/* eslint-disable @typescript-eslint/no-explicit-any */
import { ActivatedRouteSnapshot, RouteReuseStrategy, DetachedRouteHandle, UrlSegment } from '@angular/router';
import { ComponentRef, Injectable } from '@angular/core';
import equal from 'fast-deep-equal';

interface RouteStorageObject {
  snapshot: ActivatedRouteSnapshot;
  handle: DetachedRouteHandle;
  url: string;
}

@Injectable()
export class CustomReuseStrategy implements RouteReuseStrategy {
  private _storedRoutes: { [key: string]: RouteStorageObject } = {};

  private _deactivateOutlet(handle: DetachedRouteHandle): void {
    if (handle) {
      const componentRef: ComponentRef<any> = (handle as any)['componentRef'];
      if (componentRef) {
        componentRef.destroy();
      }
    }
  }

  private _createIdentifier(route: ActivatedRouteSnapshot): string {
    // Build the complete path from the root to the input route
    const segments: UrlSegment[][] = route.pathFromRoot.map((r) => r.url);
    const subpaths = ([] as UrlSegment[]).concat(...segments).map((segment) => segment.path);
    if (route.queryParams.uniqueId) {
      // Result: ${route_depth}-${path}
      return segments.length + '-' + subpaths.join('/') + '-' + `${route.queryParams.uniqueId}`;
    } else {
      // Result: ${route_depth}-${path}
      return segments.length + '-' + subpaths.join('/');
    }
  }

  private _compareObjects(base: any, compare: any): boolean {
    // loop through all properties
    for (const baseProperty in { ...base, ...compare }) {
      // determine if comparrison object has that property, if not: return false
      if (Object.prototype.hasOwnProperty.call(compare, baseProperty)) {
        switch (typeof base[baseProperty]) {
          // if one is object and other is not: return false
          // if they are both objects, recursively call this comparison function
          case 'object':
            if (
              typeof compare[baseProperty] !== 'object' ||
              !this._compareObjects(base[baseProperty], compare[baseProperty])
            ) {
              return false;
            }
            break;
          // if one is function and other is not: return false
          // if both are functions, compare function.toString() results
          case 'function':
            if (
              typeof compare[baseProperty] !== 'function' ||
              base[baseProperty].toString() !== compare[baseProperty].toString()
            ) {
              return false;
            }
            break;
          // otherwise, see if they are equal using coercive comparison
          default:
            if (base[baseProperty] != compare[baseProperty]) {
              return false;
            }
        }
      } else {
        return false;
      }
    }

    // returns true only after false HAS NOT BEEN returned through all loops
    return true;
  }
  shouldDetach(route: ActivatedRouteSnapshot): boolean {
    return route.data.reuseRoute || false;
  }

  store(route: ActivatedRouteSnapshot, handle: DetachedRouteHandle): void {
    const id = this._createIdentifier(route);
    if (route.data.reuseRoute && id.length > 0) {
      this._storedRoutes[id] = { handle, snapshot: route, url: (route as any)._routerState.url };
      //console.log('storing route', id, 'with snapshot', route, 'and handle', handle);
    }
  }

  shouldAttach(route: ActivatedRouteSnapshot): boolean {
    const id = this._createIdentifier(route);
    const storedObject = this._storedRoutes[id];
    const canAttach = !!route.routeConfig && !!storedObject;
    if (!canAttach) return false;

    const paramsMatch = this._compareObjects(route.params, storedObject.snapshot.params);
    const queryParamsMatch = this._compareObjects(route.queryParams, storedObject.snapshot.queryParams);

    // console.log('deciding to attach...', route, 'does it match?');
    // console.log('param comparison:', paramsMatch);
    // console.log('query param comparison', queryParamsMatch);
    // console.log(storedObject.snapshot, 'return: ', paramsMatch && queryParamsMatch);

    return paramsMatch && queryParamsMatch;
  }

  retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
    const id = this._createIdentifier(route);
    if (!route.routeConfig || !this._storedRoutes[id]) return null;
    return this._storedRoutes[id].handle;
  }

  shouldReuseRoute(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
    return future.routeConfig === curr.routeConfig && equal(future.params, curr.params);
  }

  deactivateAllHandles(): void {
    for (const key in this._storedRoutes) {
      this._deactivateOutlet(this._storedRoutes[key].handle);
    }
    this._storedRoutes = {};
  }
  deactivateAllHandlesExclude(exclude: string[] = []): void {
    for (const key in this._storedRoutes) {
      const obj = this._storedRoutes[key];
      if (exclude.includes(obj.url)) continue;
      this._deactivateOutlet(this._storedRoutes[key].handle);
      delete this._storedRoutes[key];
    }
  }
  deactivateHandles(url: string[]): void {
    for (const key in { ...{}, ...this._storedRoutes }) {
      const obj = this._storedRoutes[key];
      if (url.includes(obj.url)) {
        this._deactivateOutlet(this._storedRoutes[key].handle);
        delete this._storedRoutes[key];
      }
    }
  }
}
